/**
 * ErrorBoundaries.tsx — top-level error boundary components
 * Extracted from App.tsx in #2470 to reduce App.tsx to a thin shell.
 *
 * Exports:
 *   TopLevelErrorBoundary   — catches any unhandled render error
 *   ChunkLoadErrorBoundary  — catches dynamic import failures post-deploy
 */
import { Component, ReactNode } from "react";

// ---------------------------------------------------------------------------
// TopLevelErrorBoundary
// ---------------------------------------------------------------------------

export class TopLevelErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state = { error: null };
  static getDerivedStateFromError(e: Error) { return { error: e }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          minHeight: "100vh", background: "var(--brand-bg-default)", padding: "2rem",
        }}>
          <div style={{
            background: "var(--brand-bg-elevated)", border: "1px solid rgba(201,64,64,0.3)", borderRadius: "0.75rem",
            padding: "2rem 2.5rem", maxWidth: 480, width: "90%", textAlign: "center",
            fontFamily: "sans-serif",
          }}>
            <div style={{ fontSize: "1.5rem", marginBottom: "0.75rem" }}>&#9888;</div>
            <h2 style={{ color: "var(--brand-status-error)", margin: "0 0 0.5rem", fontSize: "1rem", fontWeight: 600 }}>
              Something went wrong
            </h2>
            <p style={{ color: "var(--brand-text-secondary)", fontSize: "0.85rem", margin: "0 0 1.25rem", opacity: 0.8 }}>
              {(this.state.error as Error).message}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: "rgba(95,166,95,0.15)", border: "1px solid rgba(95,166,95,0.35)",
                color: "var(--brand-status-success)", padding: "0.45rem 1.1rem", borderRadius: "0.4rem",
                cursor: "pointer", fontFamily: "inherit", fontSize: "0.85rem",
              }}
            >
              Refresh
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// ChunkLoadErrorBoundary
// ---------------------------------------------------------------------------

const CHUNK_ERR_SS_KEY = "chunk-reload-attempted";

function isChunkLoadError(e: Error): boolean {
  return (
    e.message.includes("Failed to fetch dynamically imported module") ||
    e.message.includes("Importing a module script failed") || // Safari
    e.name === "ChunkLoadError"
  );
}

// Catches lazy() import failures that occur after a deploy.
// First failure: auto-reload (gets fresh index.html + new chunk hashes).
// Second failure in same tab session: show a friendly "app updated" message
// instead of an infinite reload loop.
export class ChunkLoadErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean; isChunk: boolean }
> {
  state = { failed: false, isChunk: false };
  static getDerivedStateFromError(e: Error) {
    return { failed: true, isChunk: isChunkLoadError(e) };
  }
  componentDidCatch(e: Error) {
    if (isChunkLoadError(e)) {
      if (!sessionStorage.getItem(CHUNK_ERR_SS_KEY)) {
        sessionStorage.setItem(CHUNK_ERR_SS_KEY, "1");
        window.location.reload();
      }
      // If flag already set, stay in failed state and render the fallback below.
    }
  }
  render() {
    if (this.state.failed) {
      if (this.state.isChunk) {
        return (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            minHeight: "100vh", background: "var(--brand-bg-default)", padding: "2rem",
          }}>
            <div style={{
              background: "var(--brand-bg-elevated)", border: "1px solid rgba(247,220,111,0.25)", borderRadius: "0.75rem",
              padding: "2rem 2.5rem", maxWidth: 480, width: "90%", textAlign: "center",
              fontFamily: "sans-serif",
            }}>
              <div style={{ fontSize: "1.5rem", marginBottom: "0.75rem" }}>&#128260;</div>{/* brand-exception: decimal HTML entity for reload emoji U+1F504, not a hex colour literal */}
              <h2 style={{ color: "var(--brand-status-warning)", margin: "0 0 0.5rem", fontSize: "1rem", fontWeight: 600 }}>
                App updated
              </h2>
              <p style={{ color: "var(--brand-text-secondary)", fontSize: "0.85rem", margin: "0 0 1.25rem", opacity: 0.8 }}>
                A new version of 2hives.ai was deployed while your tab was open.
                Reload to get the latest version.
              </p>
              <button
                onClick={() => {
                  sessionStorage.removeItem(CHUNK_ERR_SS_KEY);
                  window.location.reload();
                }}
                style={{
                  background: "rgba(247,220,111,0.12)", border: "1px solid rgba(247,220,111,0.35)",
                  color: "var(--brand-status-warning)", padding: "0.45rem 1.1rem", borderRadius: "0.4rem",
                  cursor: "pointer", fontFamily: "inherit", fontSize: "0.85rem",
                }}
              >
                Reload now
              </button>
            </div>
          </div>
        );
      }
      // Non-chunk runtime error — generic fallback
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          minHeight: "100vh", background: "var(--brand-bg-default)", padding: "2rem",
        }}>
          <div style={{
            background: "var(--brand-bg-elevated)", border: "1px solid rgba(201,64,64,0.3)", borderRadius: "0.75rem",
            padding: "2rem 2.5rem", maxWidth: 480, width: "90%", textAlign: "center",
            fontFamily: "sans-serif",
          }}>
            <div style={{ fontSize: "1.5rem", marginBottom: "0.75rem" }}>&#9888;</div>
            <h2 style={{ color: "var(--brand-status-error)", margin: "0 0 0.5rem", fontSize: "1rem", fontWeight: 600 }}>
              Something went wrong
            </h2>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: "rgba(95,166,95,0.15)", border: "1px solid rgba(95,166,95,0.35)",
                color: "var(--brand-status-success)", padding: "0.45rem 1.1rem", borderRadius: "0.4rem",
                cursor: "pointer", fontFamily: "inherit", fontSize: "0.85rem",
              }}
            >
              Refresh
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
